/**
 * `UserSession` владеет `MatrixClient`, как и в Искре; ничто выше его SDK не трогает.
 * Сторы создаются от сессии и умирают вместе с ней.
 *
 * Вкладка на десктопе живёт днями, и сессия должна переживать то, чего на телефоне почти
 * не бывает: сон ноутбука, смену сети, обновление access token посреди ночи. После
 * пробуждения (`visibilitychange` + `online`) синхронизация перезапускается сразу, а не
 * ждёт таймаута длинного опроса.
 */
import {
  ClientEvent,
  createClient,
  HttpApiEvent,
  IndexedDBStore,
  MemoryStore,
  OAuth2,
  PendingEventOrdering,
  SyncState,
  TokenRefresher,
  type MatrixClient,
  type MatrixError,
  type ValidatedAuthMetadata,
} from 'matrix-js-sdk';
import { AllDevicesIsolationMode } from 'matrix-js-sdk/lib/crypto-api';
import { fetchFor } from '../auth/server';
import { MediaLoader } from '../media/media';
import { sendAttachment, uploadClientFor, type OutgoingFile } from '../media/upload';
import { ThumbnailCache } from '../media/thumbnails';
import { RoomListStore } from '../rooms/roomListStore.svelte.ts';
import { RoomDetailsStore } from '../rooms/roomDetails.svelte.ts';
import { createDirect, createGroup, existingDirect } from '../rooms/create';
import { joinByAddress, joinedRoom, profileOf, PublicRoomDirectory, searchPeople, type Person } from '../rooms/directory.svelte.ts';
import { TimelineStore } from '../timeline/timelineStore.svelte.ts';
import { canForward, forwardContent } from '../timeline/forward';
import type { Message } from '../timeline/message';
import { RecoveryStore } from '../encryption/recovery.svelte.ts';
import { SecretStorageKeyHolder } from '../encryption/secretStorageKey';
import { applyPresence, PresenceWatch } from './presence.svelte.ts';
import { IdentityChangesStore } from '../encryption/identityChanges.svelte.ts';
import { VerificationStore } from '../encryption/verification.svelte.ts';
import { deleteDatabase } from '../storage/idb';
import { fromBase64 } from '../storage/secretBox';
import { vault, type Account, type AccountSecrets } from '../storage/vault';
import { quietLogger } from '../support/logger';
import { BlockList } from './blockList.svelte.ts';
import { NotificationRules } from './notificationRules.svelte.ts';
import { ChatFolders } from './chatFolders.svelte.ts';
import { StickerLibrary } from '../stickers/stickerLibrary.svelte.ts';
import { LocalNames } from '../rooms/localNames.svelte.ts';
import { MessageIndexer, MessageSearch } from '../search/messageSearch.svelte.ts';
import { searchDatabaseName } from '../search/searchIndex';

export type Connection = 'connecting' | 'online' | 'offline';

export interface SessionEvents {
  /** Токены обновились — их надо сохранить, иначе после перезагрузки вход пропадёт. */
  onTokensRefreshed: (tokens: { accessToken: string; refreshToken?: string }) => Promise<void>;
  /**
   * Сервер перестал принимать сеанс. `soft` — он просит войти снова, а устройство оставил
   * (`soft_logout`): ключи и переписка на нём ещё годятся, и стирать их нельзя.
   */
  onLoggedOut: (soft: boolean) => void;
}

export interface SessionOptions {
  /**
   * Поднимать ли Rust-крипто. Выключают только тесты и демо, где сервер не умеет ключей.
   * Настоящий аккаунт без крипто не поднимается никогда.
   */
  crypto?: boolean;
  /** Хранить ли синхронизацию в IndexedDB. Без неё — в памяти. */
  persistent?: boolean;
}

/** Имена баз этого пользователя — чтобы при выходе удалить все. */
export function databaseNames(userId: string) {
  return {
    sync: `iskra-sync-${userId}`,
    cryptoPrefix: `iskra-crypto-${userId}`,
  };
}

export interface AccountDevice {
  id: string;
  name: string;
  lastSeen?: number;
  ip?: string;
  current: boolean;
}

/** Чем кончилось «Завершить»: спросить пароль, пароль не тот, это на сайте сервера, не вышло. */
export type EndSessions = 'done' | 'needsPassword' | 'wrongPassword' | 'managed' | 'failed';

export class UserSession {
  connection = $state<Connection>('connecting');
  /** Как меня зовут и как я выгляжу — живое: меняется, когда поменяли в настройках или где-то ещё. */
  profile = $state<{ id: string; name: string; avatarUrl?: string }>({ id: '', name: '' });
  /** Первая синхронизация прошла — список комнат уже есть, хотя бы из кэша. */
  ready = $state(false);

  readonly userId: string;
  /** Список чатов — живёт, пока жива сессия. */
  readonly rooms: RoomListStore;
  /** Аватарки и миниатюры: с токеном, в памяти, с потолком. */
  readonly thumbnails: ThumbnailCache;
  /** Вложения: превью в кэше, оригиналы — тому, кто показывает. */
  readonly media: MediaLoader;
  /** Код восстановления и доступ к старой переписке. `null` — сессия без крипто (тесты). */
  readonly recovery: RecoveryStore | null;
  /** Сверка эмодзи с другим устройством. */
  readonly verification: VerificationStore | null;
  /** Заблокированные — игнор-список аккаунта. */
  readonly blocked: BlockList;
  /** Уведомления всего аккаунта — push rules на сервере. */
  readonly notificationRules: NotificationRules;
  /** Папки чатов — в данных аккаунта, общие для всех устройств. */
  readonly folders: ChatFolders;
  /** Как я зову собеседников на этом устройстве. */
  readonly localNames: LocalNames;
  /** Стикеры аккаунта: свой набор, подписки, недавние, «Искорки». */
  readonly stickers: StickerLibrary;
  /** Поисковый индекс сообщений — на устройстве, зашифрованный (`core/search/`). */
  readonly searchIndex: MessageIndexer;
  /** Поиск по сообщениям над списком чатов. */
  readonly search: MessageSearch;
  private readonly client: MatrixClient;
  private readonly account: Account;
  private readonly detach: Array<() => void> = [];
  private stopped = false;

  /** Сервер, с которым клиент разговаривает на самом деле — после поиска по имени аккаунта. */
  get homeserver(): string {
    return this.client.getHomeserverUrl();
  }

  private constructor(client: MatrixClient, account: Account, keys: SecretStorageKeyHolder | null, persistent: boolean) {
    this.client = client;
    this.account = account;
    this.userId = account.userId;
    this.localNames = new LocalNames(account.userId);
    this.rooms = new RoomListStore(client, this.localNames);
    this.thumbnails = new ThumbnailCache({
      httpUrl: (mxc, size) => client.mxcUrlToHttp(mxc, size, size, 'crop', false, true, true),
      accessToken: () => client.getAccessToken(),
      fetch: fetchFor(account.method === 'demo'),
    });
    this.media = new MediaLoader({
      downloadUrl: (mxc) => client.mxcUrlToHttp(mxc, undefined, undefined, undefined, false, true, true),
      thumbnailUrl: (mxc, size) => client.mxcUrlToHttp(mxc, size, size, 'scale', false, true, true),
      accessToken: () => client.getAccessToken(),
      fetch: fetchFor(account.method === 'demo'),
    });
    this.recovery = keys ? new RecoveryStore(client, keys) : null;
    this.verification = keys ? new VerificationStore(client) : null;
    this.blocked = new BlockList(client);
    this.notificationRules = new NotificationRules(client);
    this.folders = new ChatFolders(client);
    const uploader = uploadClientFor(client, account.method === 'demo' ? fetchFor(true) : undefined);
    this.stickers = new StickerLibrary(client, (blob, type) => uploader.upload(blob, { type }));
    this.searchIndex = new MessageIndexer(client, persistent ? searchDatabaseName(account.userId) : null);
    this.search = new MessageSearch(this.searchIndex, this.senderName);
  }

  /**
   * Кто написал найденное. В личном чате с локальным именем собеседник и здесь «Мама», а не
   * `anna_1987`, — но только он сам: третий, позванный в чат, своё имя сохраняет.
   */
  private readonly senderName = (roomId: string, senderId: string): string => {
    const local = this.localNames.get(roomId);
    const summary = this.rooms.get(roomId);
    if (local && summary?.kind === 'direct' && summary.avatarSeed === senderId) return local;
    return this.client.getRoom(roomId)?.getMember(senderId)?.name || this.client.getUser(senderId)?.displayName || senderId;
  };

  /** Поиск в одном чате. Живёт, пока открыто его окно. */
  roomSearch(roomId: string): MessageSearch {
    return new MessageSearch(this.searchIndex, this.senderName, roomId);
  }

  /** Сколько места занимает поисковый индекс — для «Хранилища». */
  searchIndexSize(): Promise<number> {
    return this.searchIndex.size();
  }

  /** «Очистить поисковый индекс»: соберётся заново из того, что откроют и пролистают. */
  clearSearchIndex(): Promise<void> {
    return this.searchIndex.clear();
  }

  /** Лента комнаты. Живёт, пока открыт чат: тот, кто открыл, её и `destroy()`. */
  timeline(roomId: string, threadRootId?: string): TimelineStore {
    return new TimelineStore(this.client, roomId, uploadClientFor(this.client, this.account.method === 'demo' ? fetchFor(true) : undefined), threadRootId);
  }

  /** Где собеседник — «в сети», «был(а) …». Живёт, пока открыт его чат. */
  presence(userId: string): PresenceWatch {
    return new PresenceWatch(this.client, userId);
  }

  /** Время захода скрыто или нет — сказать серверу (см. `presence.svelte.ts`). */
  applyPresence(hidden: boolean): Promise<void> {
    return applyPresence(this.client, hidden);
  }

  /** Кто в комнате сменил ключи. Живёт, пока открыт чат, — как и лента. */
  identityChanges(roomId: string): IdentityChangesStore {
    return new IdentityChangesStore(this.client, roomId);
  }

  /**
   * Переслать сообщения в чаты — по порядку ленты, в каждый чат по очереди; комментарий —
   * следом, своим сообщением (порядок Telegram: сначала что передают, потом что о нём сказать).
   *
   * Слова уходят с пометкой `m.forwarded` (`forward.ts`). Фото, видео и файлы — байтами заново,
   * через шифрующую загрузку, а не копией адреса: `mxc://` из чужого чата получателям не открыть
   * своими ключами, а зашифрованное в открытый чат ушло бы без шифра. `false` — что-то не ушло.
   */
  async forward(messages: readonly Message[], roomIds: readonly string[], comment = ''): Promise<boolean> {
    const uploader = uploadClientFor(this.client, this.account.method === 'demo' ? fetchFor(true) : undefined);
    let allSent = true;
    const words = comment.trim();
    for (const roomId of roomIds) {
      for (const message of messages.filter(canForward)) {
        try {
          const content = forwardContent(message);
          if (content) {
            await this.client.sendMessage(roomId, content as never);
            continue;
          }
          const kind = message.kind;
          if (kind.type !== 'image' && kind.type !== 'video' && kind.type !== 'file') continue;
          const { attachment } = kind;
          const blob = await this.media.blob(attachment.source!, attachment.mimetype);
          const file: OutgoingFile = {
            blob,
            name: attachment.name,
            mimetype: attachment.mimetype ?? (blob.type || 'application/octet-stream'),
            kind: kind.type,
            ...(attachment.width ? { width: attachment.width } : {}),
            ...(attachment.height ? { height: attachment.height } : {}),
            ...(attachment.duration ? { duration: attachment.duration } : {}),
          };
          // Подпись — своими словами, без HTML: в нём могут быть упомянуты те, кого в том чате нет.
          await sendAttachment(uploader, roomId, file, attachment.caption ? { caption: attachment.caption } : {});
        } catch {
          allSent = false;
        }
      }
      if (words) {
        await this.client.sendMessage(roomId, { msgtype: 'm.text', body: words } as never).catch(() => (allSent = false));
      }
    }
    return allSent;
  }

  private uploadLimitValue: Promise<number | undefined> | undefined;

  /** Сколько сервер примет одним файлом (`m.upload.size`) — или «не сказал». */
  uploadLimit(): Promise<number | undefined> {
    this.uploadLimitValue ??= this.client
      .getMediaConfig()
      .then((config) => (typeof config['m.upload.size'] === 'number' ? config['m.upload.size'] : undefined))
      .catch(() => undefined);
    return this.uploadLimitValue;
  }

  /**
   * Черновики аккаунта — для строк списка: «Черновик: …» на месте последнего сообщения, как в
   * нативной. Живут здесь, а не в сводке комнаты: SDK о них не знает и не скажет, что поменялось.
   */
  drafts = $state.raw<Record<string, string>>({});

  /** Черновик чата — под ключом аккаунта (`vault`). Не прочитался — пусто, а не ошибка. */
  async draft(roomId: string): Promise<string> {
    return vault.draft(this.userId, roomId).catch(() => '');
  }

  /** Пустой — стереть. Не записался — не повод мешать человеку печатать. */
  async saveDraft(roomId: string, text: string): Promise<void> {
    const trimmed = text.trim() ? text : '';
    if ((this.drafts[roomId] ?? '') !== trimmed) {
      const next = { ...this.drafts };
      if (trimmed) next[roomId] = trimmed;
      else delete next[roomId];
      this.drafts = next;
    }
    await vault.saveDraft(this.userId, roomId, text).catch(() => {});
  }

  /** Можно ли писать в комнату: вошли, а не только приглашены. */
  canSend(roomId: string): boolean {
    return this.client.getRoom(roomId)?.getMyMembership() === 'join';
  }

  /** Профиль — с сервера: он источник, а не то, что SDK собрал из событий комнат. */
  async readProfile(): Promise<void> {
    this.profile = this.me();
    try {
      const info = await this.client.getProfileInfo(this.userId);
      this.profile = {
        id: this.userId,
        name: info.displayname || this.userId,
        ...(info.avatar_url ? { avatarUrl: info.avatar_url } : {}),
      };
    } catch {
      // Нет сети — останется то, что знает SDK.
    }
  }

  /** Как меня зовут и как я выгляжу — для своего лица в группе лиц. */
  me(): { id: string; name: string; avatarUrl?: string } {
    const user = this.client.getUser(this.userId);
    return {
      id: this.userId,
      name: user?.displayName || this.userId,
      ...(user?.avatarUrl ? { avatarUrl: user.avatarUrl } : {}),
    };
  }

  // ————— Комнаты: «О чате» и новые —————

  /** «О чате» комнаты. Живёт, пока открыта панель: тот, кто открыл, и `destroy()`. */
  roomDetails(roomId: string): RoomDetailsStore {
    return new RoomDetailsStore(this.client, roomId, (id) => this.rooms.get(id)?.kind === 'direct', this.localNames);
  }

  createDirect(userId: string): Promise<string> {
    return createDirect(this.client, userId);
  }

  createGroup(name: string, open: boolean, topic = ''): Promise<string> {
    return createGroup(this.client, name, open, topic);
  }

  /** Люди в каталоге сервера — для поиска в «Новом чате». */
  searchPeople(term: string): Promise<Person[]> {
    return searchPeople(this.client, term);
  }

  /** Каталог открытых комнат. Живёт, пока открыт его экран. */
  publicRooms(): PublicRoomDirectory {
    return new PublicRoomDirectory(this.client);
  }

  /** Войти в комнату по адресу или ID — или из каталога; `via` — для ID из чужой ссылки. */
  joinRoom(address: string, via: string[] = []): Promise<string> {
    return joinByAddress(this.client, address, via);
  }

  /** Куда ссылка ведёт без вопроса: личный чат с человеком уже есть, в комнате уже состоим. */
  existingRoomFor(target: string): string | undefined {
    return target.startsWith('@') ? existingDirect(this.client, target) : joinedRoom(this.client, target);
  }

  /** Имя и фото человека — для вопроса «Написать …?». */
  profileOf(userId: string): Promise<Person> {
    return profileOf(this.client, userId);
  }

  // ————— Профиль, устройства, аккаунт —————

  /**
   * Загрузить картинку открыто — для фото профиля и комнаты. Они публичны по устройству
   * Matrix (лежат в состоянии, которое не шифруется), поэтому и шифровать их незачем.
   */
  async uploadPublic(blob: Blob, name: string, type: string): Promise<string> {
    const uploader = uploadClientFor(this.client, this.account.method === 'demo' ? fetchFor(true) : undefined);
    return uploader.upload(blob, { name, type });
  }

  async setName(name: string): Promise<void> {
    await this.client.setDisplayName(name.trim());
    await this.readProfile();
  }

  /** Фото профиля; `null` — убрать. */
  async setPhoto(photo: { blob: Blob; name: string; type: string } | null): Promise<void> {
    const mxc = photo ? await this.uploadPublic(photo.blob, photo.name, photo.type) : '';
    await this.client.setAvatarUrl(mxc);
    await this.readProfile();
  }

  /** Мои устройства — список с сервера; это первым, остальные — по последней активности. */
  async devices(): Promise<AccountDevice[]> {
    const { devices } = await this.client.getDevices();
    const mine = this.client.getDeviceId();
    return devices
      .map((d) => ({
        id: d.device_id,
        name: d.display_name || d.device_id,
        ...(d.last_seen_ts ? { lastSeen: d.last_seen_ts } : {}),
        ...(d.last_seen_ip ? { ip: d.last_seen_ip } : {}),
        current: d.device_id === mine,
      }))
      .sort((a, b) => +b.current - +a.current || (b.lastSeen ?? 0) - (a.lastSeen ?? 0));
  }

  /** Сеансы аккаунта завершаются на странице сервера (вход через MAS), а не здесь. */
  get sessionsManagedByServer(): boolean {
    return !!this.account.oauth;
  }

  /**
   * Страница сервера, где завершают сеанс (MSC4191): один — сразу на нём, несколько — список.
   * Нет страницы — `undefined`.
   */
  sessionEndPage(deviceId?: string): string | undefined {
    const page = this.accountPage();
    if (!page) return undefined;
    // Разовый расчёт адреса, не состояние.
    // eslint-disable-next-line svelte/prefer-svelte-reactivity
    const url = new URL(page);
    if (deviceId) {
      url.searchParams.set('action', 'org.matrix.session_end');
      url.searchParams.set('device_id', deviceId);
    } else {
      url.searchParams.set('action', 'org.matrix.sessions_list');
    }
    return url.href;
  }

  /**
   * Завершить сеансы — выйти из аккаунта на тех устройствах (`AccountDevices.end` нативной).
   * Сначала без пароля: некоторым серверам он не нужен, а ответ скажет, нужен ли. Это
   * устройство не завершается никогда: выход — другая кнопка.
   */
  async endSessions(deviceIds: string[], password?: string): Promise<EndSessions> {
    const others = deviceIds.filter((id) => id !== this.client.getDeviceId());
    if (!others.length) return 'done';
    if (this.sessionsManagedByServer) return 'managed';
    const refused = (error: unknown): EndSessions => {
      const e = error as { httpStatus?: number; errcode?: string; data?: { errcode?: string } };
      const errcode = e.errcode ?? e.data?.errcode;
      if (e.httpStatus === 404 || errcode === 'M_UNRECOGNIZED') return 'managed';
      if (e.httpStatus === 401) return password ? 'wrongPassword' : 'needsPassword';
      return 'failed';
    };
    try {
      if (!password) {
        await this.client.deleteMultipleDevices(others);
        return 'done';
      }
      // Пароль — в сессию UIA, которую сервер выдаёт на первый запрос без него.
      let session: string | undefined;
      try {
        await this.client.deleteMultipleDevices(others);
        return 'done';
      } catch (error) {
        session = (error as { data?: { session?: string } }).data?.session;
        if ((error as { httpStatus?: number }).httpStatus !== 401) return refused(error);
      }
      await this.client.deleteMultipleDevices(others, {
        type: 'm.login.password',
        identifier: { type: 'm.id.user', user: this.userId },
        password,
        ...(session ? { session } : {}),
      } as never);
      return 'done';
    } catch (error) {
      return refused(error);
    }
  }

  /**
   * Страница аккаунта у сервера (`account_management_uri` MAS) — там живут устройства и
   * выход из них: на серверах с MAS удалить устройство через клиентский API нельзя.
   */
  accountPage(): string | undefined {
    const uri = this.account.oauth?.metadata['account_management_uri'];
    return typeof uri === 'string' && /^https:\/\//.test(uri) ? uri : undefined;
  }

  /**
   * «Очистить кэш»: кэш синхронизации — вон, ключи шифрования и вход — остаются. После
   * этого страницу перезагружают: клиент начнёт с первой синхронизации.
   */
  async clearCache(): Promise<void> {
    this.stop();
    // Индекс — вместе с кэшем, как в нативной: в нём могут оставаться слова удалённого.
    await deleteDatabase(searchDatabaseName(this.userId)).catch(() => {});
    try {
      await this.client.store.deleteAllData();
    } catch {
      await deleteDatabase(`matrix-js-sdk:${databaseNames(this.userId).sync}`).catch(() => {});
    }
  }

  /** Способ входа — для экрана «О приложении» и «Хранилища»: демо не хранится между входами. */
  get method(): Account['method'] {
    return this.account.method;
  }

  static async start(
    account: Account,
    secrets: AccountSecrets,
    events: SessionEvents,
    options: SessionOptions = {},
  ): Promise<UserSession> {
    const { crypto = true, persistent = true } = options;
    const names = databaseNames(account.userId);
    const logger = quietLogger();
    const demo = account.method === 'demo';

    const store =
      persistent && typeof indexedDB !== 'undefined'
        ? new IndexedDBStore({ indexedDB, dbName: names.sync })
        : new MemoryStore();

    let tokenRefreshFunction;
    if (account.oauth && secrets.refreshToken) {
      const oauth = new OAuth2(
        account.oauth.metadata as unknown as ValidatedAuthMetadata,
        { clientId: account.oauth.clientId, redirectUri: account.oauth.redirectUri, deviceId: account.deviceId },
        logger,
      );
      const refresher = new TokenRefresher(refusalsOnly(oauth), (tokens) =>
        events.onTokensRefreshed({
          accessToken: tokens.accessToken,
          ...(tokens.refreshToken ? { refreshToken: tokens.refreshToken } : {}),
        }),
      );
      tokenRefreshFunction = refresher.tokenRefreshFunction;
    }

    // Ключ секретного хранилища — только на время операции, см. `SecretStorageKeyHolder`.
    const keys = crypto ? new SecretStorageKeyHolder() : null;
    const client = createClient({
      baseUrl: account.homeserverUrl,
      ...(keys ? { cryptoCallbacks: keys.callbacks } : {}),
      userId: account.userId,
      deviceId: account.deviceId,
      accessToken: secrets.accessToken,
      ...(secrets.refreshToken ? { refreshToken: secrets.refreshToken } : {}),
      ...(tokenRefreshFunction ? { tokenRefreshFunction } : {}),
      fetchFn: fetchFor(demo),
      store,
      logger,
      timelineSupport: true,
    });

    await store.startup();
    if (crypto) {
      await client.initRustCrypto({
        storageKey: fromBase64(secrets.cryptoStorageKey),
        cryptoDatabasePrefix: names.cryptoPrefix,
        useIndexedDB: persistent,
      });
      // Как нативная: у проверенного собеседника сменилась личность или появилось не
      // подписанное им устройство — сообщение не уходит, пока читающий не решит
      // «Отправить всё равно» (`TimelineStore.sendAnyway`). По умолчанию SDK молча шифрует.
      client.getCrypto()?.setDeviceIsolationMode(new AllDevicesIsolationMode(true));
    }

    const session = new UserSession(client, account, keys, persistent);
    session.listen(events);
    // До синхронизации: иначе список на миг покажет `anna_1987`, а потом «Маму».
    await session.localNames.load();
    session.drafts = await vault.drafts(account.userId).catch(() => ({}));
    await client.startClient({
      initialSyncLimit: 20,
      lazyLoadMembers: true,
      // Не ушедшие — отдельно от ленты и в её конце, пока не уйдут: «Не отправлено» не
      // должно тонуть в истории, если сервер тем временем прислал новое.
      pendingEventOrdering: PendingEventOrdering.Detached,
    });
    return session;
  }

  private listen(events: SessionEvents): void {
    const onSync = (state: SyncState) => {
      if (state === SyncState.Prepared || state === SyncState.Syncing || state === SyncState.Catchup) {
        this.connection = 'online';
        if (!this.ready) void this.readProfile();
        this.ready = true;
      } else if (state === SyncState.Error || state === SyncState.Reconnecting) {
        this.connection = 'offline';
      }
    };
    this.client.on(ClientEvent.Sync, onSync);
    this.detach.push(() => this.client.off(ClientEvent.Sync, onSync));

    const onLoggedOut = (error: MatrixError) => {
      if (this.stopped) return;
      // С refresh-токеном SDK сначала пробует обновиться, и выход — это отказ в обновлении:
      // сеанс завершён, а `soft_logout` в ответе — про прежний, просто истёкший токен.
      events.onLoggedOut(error?.data?.['soft_logout'] === true && !this.client.getRefreshToken());
    };
    this.client.on(HttpApiEvent.SessionLoggedOut, onLoggedOut);
    this.detach.push(() => this.client.off(HttpApiEvent.SessionLoggedOut, onLoggedOut));

    if (typeof window === 'undefined') return;
    // Проснулись или вернулась сеть — не ждём таймаута длинного опроса.
    const wake = () => {
      if (document.visibilityState === 'visible' && navigator.onLine !== false) this.client.retryImmediately();
    };
    const offline = () => (this.connection = 'offline');
    document.addEventListener('visibilitychange', wake);
    window.addEventListener('online', wake);
    window.addEventListener('offline', offline);
    this.detach.push(() => {
      document.removeEventListener('visibilitychange', wake);
      window.removeEventListener('online', wake);
      window.removeEventListener('offline', offline);
    });
  }

  /** Повторить сейчас — кнопка «Повторить» на полосе «Нет соединения». */
  retry(): void {
    this.client.retryImmediately();
  }

  /** Остановить, ничего не удаляя: другая вкладка забрала сессию, или страница уходит. */
  stop(): void {
    if (this.stopped) return;
    this.stopped = true;
    for (const off of this.detach.splice(0)) off();
    this.rooms.destroy();
    this.blocked.destroy();
    this.notificationRules.destroy();
    this.folders.destroy();
    this.stickers.destroy();
    this.search.destroy();
    this.searchIndex.destroy();
    this.verification?.destroy();
    this.thumbnails.clear();
    this.media.clear();
    this.client.stopClient();
  }

  /**
   * Выход: сеанс на сервере завершается, и на устройстве не остаётся ничего — ни баз,
   * ни ключей. Без сети сервер не узнает, но локально всё равно всё удаляется: человек
   * просил выйти, а не «выйти, когда получится».
   */
  async signOut(): Promise<void> {
    const oauth = this.account.oauth;
    try {
      if (oauth) {
        const auth = new OAuth2(oauth.metadata as unknown as ValidatedAuthMetadata, {
          clientId: oauth.clientId,
          redirectUri: oauth.redirectUri,
          deviceId: this.account.deviceId,
        });
        const token = this.client.getAccessToken();
        const refresh = this.client.getRefreshToken();
        // Каждый отдельно: не отозвался refresh — access всё равно отзываем.
        await Promise.allSettled([
          ...(refresh ? [auth.revokeToken(refresh, 'refresh_token')] : []),
          ...(token ? [auth.revokeToken(token, 'access_token')] : []),
        ]);
      } else {
        await this.client.logout(false);
      }
    } catch {
      // Сервер не ответил — сеанс там останется, пока его не завершат с другого устройства.
    }
    await this.wipe();
  }

  /** Удаляет всё локальное: кэш синхронизации и криптохранилище. */
  async wipe(): Promise<void> {
    this.stop();
    await wipeUserData(this.account.userId, this.client);
  }
}

/**
 * Обновление токена, которому сервер сказал «нет», SDK считает выходом — на любой 4xx, и сеанс
 * завершается со стиранием всего локального, ключей шифрования тоже. Но 429 («слишком часто»)
 * и 408 — не отказ, а «попозже». Отказ — только ответ по RFC 6749, §5.2: 400 (`invalid_grant`)
 * или 401. Остальное уходит наверх не `HTTPError`-ом, и SDK повторит обновление позже, как
 * после сбоя сети.
 */
export function refusalsOnly(oauth: OAuth2): OAuth2 {
  const grant = oauth.performRefreshTokenGrant.bind(oauth);
  oauth.performRefreshTokenGrant = async (refreshToken) => {
    try {
      return await grant(refreshToken);
    } catch (error) {
      const status = (error as { httpStatus?: unknown } | null)?.httpStatus;
      if (typeof status === 'number' && status !== 400 && status !== 401) {
        throw new Error(`token refresh deferred (${status})`, { cause: error });
      }
      throw error;
    }
  };
  return oauth;
}

/** Удаляет базы пользователя; с клиентом — его руками, без — по именам. */
export async function wipeUserData(userId: string, client?: MatrixClient): Promise<void> {
  const names = databaseNames(userId);
  if (client) {
    try {
      await client.clearStores({ cryptoDatabasePrefix: names.cryptoPrefix });
    } catch {
      // Падаем ниже на удаление по именам.
    }
  }
  if (typeof indexedDB === 'undefined') return;
  // Каждая сама по себе: одна не удалилась — остальные всё равно удаляются, а ошибка — наверх.
  const results = await Promise.allSettled([
    deleteDatabase(`matrix-js-sdk:${names.sync}`),
    deleteDatabase(`${names.cryptoPrefix}::matrix-sdk-crypto`),
    deleteDatabase(`${names.cryptoPrefix}::matrix-sdk-crypto-meta`),
    deleteDatabase(searchDatabaseName(userId)),
  ]);
  const failed = results.find((result) => result.status === 'rejected');
  if (failed) throw failed.reason;
}
